/**
 * Utility functions for parsing and formatting addresses
 */

/**
 * Parse an unparsed address string into street address and city/state/zip components
 * Handles cases where unit numbers might be separated by commas
 * 
 * @param {string} unparsedAddress - The raw unparsed address string
 * @param {string} city - The city name (used to identify where city/state/zip starts)
 * @param {string} state - The state name (used as additional validation)
 * @returns {Object} Object with streetAddress and cityStateZip properties
 */
export const parseUnparsedAddress = (unparsedAddress, city = '', state = '') => {
  if (!unparsedAddress) {
    return {
      streetAddress: 'Street address unavailable',
      cityStateZip: 'Location unavailable'
    };
  }

  // Handle the specific case where the address is formatted as:
  // "334 SW Roosevelt Avenue, 3, Bend, OR 97702"
  // We need to find where the city starts and split there
  
  if (city) {
    const cityIndex = unparsedAddress.toLowerCase().indexOf(city.toLowerCase());
    if (cityIndex > 0) {
      // Everything before the city is the street address (including unit numbers)
      const streetAddress = unparsedAddress.substring(0, cityIndex).trim().replace(/,$/, '');
      // Everything from the city onwards is city/state/zip
      const cityStateZip = unparsedAddress.substring(cityIndex).trim();
      return {
        streetAddress: streetAddress || 'Street address unavailable',
        cityStateZip: cityStateZip || 'Location unavailable'
      };
    }
  }

  // Fallback: split by comma and use heuristics to determine what's street vs city/state/zip
  const parts = unparsedAddress.split(',');
  
  if (parts.length === 1) {
    // No commas, treat as street address
    return {
      streetAddress: unparsedAddress.trim(),
      cityStateZip: 'Location unavailable'
    };
  }

  if (parts.length === 2) {
    // Two parts: likely "street, city state zip"
    return {
      streetAddress: parts[0].trim(),
      cityStateZip: parts[1].trim()
    };
  }

  // Three or more parts: need to determine where street ends and city begins
  // Look for patterns that indicate city/state/zip (contains state abbreviations, zip codes, etc.)
  let streetParts = [];
  let cityStateZipParts = [];
  let foundCityStateZip = false;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    
    if (!foundCityStateZip) {
      // Check if this part looks like it contains city/state/zip
      const hasStateAbbrev = /\b[A-Z]{2}\b/.test(part); // Contains 2-letter state abbreviation
      const hasZipCode = /\b\d{5}(-\d{4})?\b/.test(part); // Contains zip code
      const hasCityName = city && part.toLowerCase().includes(city.toLowerCase());
      
      if (hasStateAbbrev || hasZipCode || hasCityName) {
        // This part and all following parts are city/state/zip
        cityStateZipParts = parts.slice(i);
        foundCityStateZip = true;
      } else {
        // This part is still part of the street address
        streetParts.push(part);
      }
    } else {
      // We've already found the city/state/zip section
      cityStateZipParts.push(part);
    }
  }

  const streetAddress = streetParts.join(', ').trim();
  const cityStateZip = cityStateZipParts.join(', ').trim();

  return {
    streetAddress: streetAddress || 'Street address unavailable',
    cityStateZip: cityStateZip || 'Location unavailable'
  };
};

/**
 * Parse address from various sources with proper fallbacks
 * 
 * @param {Object} listing - The listing object containing address data
 * @returns {Object} Object with streetAddress and cityStateZip properties
 */
export const getAddressParts = (listing) => {
  let streetAddress = '';
  let cityStateZip = '';

  // Check if we have an address field (which contains the unparsed address)
  if (listing.address) {
    // Use the improved unparsed address parser for the address field
    const parsed = parseUnparsedAddress(
      listing.address, 
      listing.city || listing.raw?.City, 
      listing.state || listing.raw?.StateOrProvince
    );
    streetAddress = parsed.streetAddress;
    cityStateZip = parsed.cityStateZip;
  } else if (listing.unparsedAddress) {
    // Use the improved unparsed address parser
    const parsed = parseUnparsedAddress(
      listing.unparsedAddress, 
      listing.city || listing.raw?.City, 
      listing.state || listing.raw?.StateOrProvince
    );
    streetAddress = parsed.streetAddress;
    cityStateZip = parsed.cityStateZip;
  } else {
    // Fallback: build from raw fields
    streetAddress = listing.raw?.StreetNumber && listing.raw?.StreetName
      ? `${listing.raw.StreetNumber} ${listing.raw?.StreetDirPrefix || ''} ${listing.raw.StreetName} ${listing.raw?.StreetSuffix || ''}`.replace(/\s+/g, ' ').trim()
      : '';

    const city = listing.city || listing.raw?.City || '';
    const state = listing.state || listing.raw?.StateOrProvince || '';
    const postalCode = listing.postalCode || listing.raw?.PostalCode || '';
    cityStateZip = [city, state, postalCode].filter(Boolean).join(' ').trim();
  }
  
  return {
    streetAddress: streetAddress || 'Street address unavailable',
    cityStateZip: cityStateZip || 'Location unavailable'
  };
};
